import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaStar, FaQuoteLeft, FaRegStar, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Styled Components
const SectionContainer = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #f7f9fc 0%, #f1f5f9 100%);
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const SectionTitle = styled.h2`
  font-size: 3rem;
  font-weight: 700;
  background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
  animation: ${fadeIn} 0.8s ease-out;
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: #64748b;
  max-width: 600px;
  margin: 0 auto 3rem;
  animation: ${fadeIn} 0.8s ease-out 0.2s both;
`;

const TestimonialContainer = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2.5rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const TestimonialCard = styled.div`
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 20px;
  box-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.08),
    0 2px 8px rgba(0, 0, 0, 0.02);
  padding: 2.5rem;
  position: relative;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 1px solid rgba(255, 255, 255, 0.8);
  animation: ${fadeIn} 0.6s ease-out ${props => props.delay || '0s'} both;
  cursor: pointer;

  &:hover {
    transform: translateY(-12px) scale(1.02);
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.12),
      0 8px 24px rgba(0, 0, 0, 0.06);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #3498db, #2c3e50);
    border-radius: 20px 20px 0 0;
  }
`;

const QuoteIcon = styled(FaQuoteLeft)`
  color: #e2e8f0;
  font-size: 2.5rem;
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  opacity: 0.7;
  transition: all 0.3s ease;

  ${TestimonialCard}:hover & {
    color: #3498db;
    opacity: 1;
    transform: scale(1.1);
  }
`;

const ReviewText = styled.p`
  font-size: 1.05rem;
  color: #475569;
  line-height: 1.7;
  font-style: italic;
  margin: 1.5rem 0 2rem;
  text-align: left;
  position: relative;
  z-index: 2;
`;

const ReviewerInfo = styled.div`
  margin-top: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  animation: ${slideIn} 0.5s ease-out;
`;

const ReviewerAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3498db, #2c3e50);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1.2rem;
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
`;

const ReviewerDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
`;

const ReviewerName = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.3rem;
`;

const ReviewerTitle = styled.span`
  font-size: 0.9rem;
  color: #64748b;
  font-style: italic;
`;

const StarRating = styled.div`
  display: flex;
  gap: 0.2rem;
  margin-top: 0.5rem;
  
  svg {
    font-size: 1.1rem;
    transition: transform 0.2s ease;
    
    &:hover {
      transform: scale(1.2);
    }
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 3rem;
`;

const NavButton = styled.button`
  background: linear-gradient(135deg, #3498db, #2c3e50);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(52, 152, 219, 0.3);

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(52, 152, 219, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 2rem auto;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 1rem 2rem;
  border-radius: 10px;
  margin: 2rem auto;
  max-width: 500px;
  animation: ${fadeIn} 0.5s ease-out;
`;

const defaultReviews = [
  {
    author_name: "Sarah Johnson",
    rating: 5,
    text: "Outstanding service and a truly professional team. Their attention to detail and swift execution helped us achieve remarkable results. Highly recommended!",
    position: "Marketing Director",
    company: "Tech Innovations Inc"
  },
  {
    author_name: "Michael Chen",
    rating: 5,
    text: "A seamless experience from start to finish. The team's expertise and dedication transformed our digital presence. Will definitely be a returning client.",
    position: "CEO",
    company: "StartUp Ventures"
  },
  {
    author_name: "Emily Rodriguez",
    rating: 4,
    text: "Great results and excellent communication throughout the project. They delivered beyond our expectations and provided valuable insights that helped our business grow.",
    position: "Product Manager",
    company: "Digital Solutions Co"
  },
  {
    author_name: "David Thompson",
    rating: 5,
    text: "Working with this team has been exceptional. Their creative approach and technical expertise resulted in a solution that perfectly matches our vision.",
    position: "CTO",
    company: "Future Tech Labs"
  },
  {
    author_name: "Lisa Wang",
    rating: 5,
    text: "Professional, responsive, and highly skilled. They understood our needs perfectly and delivered a solution that has significantly improved our operations.",
    position: "Operations Director",
    company: "Global Retail Corp"
  },
  {
    author_name: "James Wilson",
    rating: 4,
    text: "Reliable partner that consistently delivers quality work. Their team is knowledgeable and always available to provide support and guidance when needed.",
    position: "Business Owner",
    company: "Wilson Enterprises"
  }
];

const TestimonialSection = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Simulate API call with timeout
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // For demo purposes, we'll use default reviews
        // In production, you would fetch from your API
        const response = await fetch('/api/google-reviews');
        if (!response.ok) {
          throw new Error('Failed to fetch reviews');
        }
        const data = await response.json();
        
        if (data.reviews && data.reviews.length > 0) {
          setReviews(data.reviews);
        } else {
          setReviews(defaultReviews);
        }
      } catch (error) {
        console.error("Error fetching reviews:", error);
        setError("Unable to load testimonials. Showing sample reviews.");
        setReviews(defaultReviews);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => {
      if (index < rating) {
        return <FaStar key={index} color="#FFD700" />;
      }
      return <FaRegStar key={index} color="#E2E8F0" />;
    });
  };

  const nextTestimonial = () => {
    setCurrentIndex(prev => (prev + 1) % reviews.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex(prev => (prev - 1 + reviews.length) % reviews.length);
  };

  if (loading) {
    return (
      <SectionContainer>
        <SectionTitle>What Our Clients Say</SectionTitle>
        <LoadingSpinner />
      </SectionContainer>
    );
  }

  return (
    <SectionContainer ref={containerRef}>
      <SectionTitle>Client Testimonials</SectionTitle>
      <SectionSubtitle>
        Discover why businesses trust us with their digital success. 
        Here's what our clients have to say about their experience.
      </SectionSubtitle>

      {error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}

      <TestimonialContainer>
        <TestimonialGrid>
          {reviews.slice(currentIndex, currentIndex + 3).map((review, index) => (
            <TestimonialCard 
              key={`${review.author_name}-${index}`}
              delay={`${index * 0.1}s`}
            >
              <QuoteIcon />
              <ReviewText>{review.text}</ReviewText>
              <ReviewerInfo>
                <ReviewerAvatar>
                  {getInitials(review.author_name)}
                </ReviewerAvatar>
                <ReviewerDetails>
                  <ReviewerName>{review.author_name}</ReviewerName>
                  {review.position && review.company && (
                    <ReviewerTitle>
                      {review.position}, {review.company}
                    </ReviewerTitle>
                  )}
                  <StarRating>
                    {renderStars(review.rating)}
                  </StarRating>
                </ReviewerDetails>
              </ReviewerInfo>
            </TestimonialCard>
          ))}
        </TestimonialGrid>

        {reviews.length > 3 && (
          <NavigationButtons>
            <NavButton onClick={prevTestimonial} disabled={currentIndex === 0}>
              <FaChevronLeft />
            </NavButton>
            <NavButton 
              onClick={nextTestimonial} 
              disabled={currentIndex >= reviews.length - 3}
            >
              <FaChevronRight />
            </NavButton>
          </NavigationButtons>
        )}
      </TestimonialContainer>
    </SectionContainer>
  );
};

export default TestimonialSection;